import React, { useState } from "react";
import { Phone, Plus, X } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

export function NurseCallScheduler({ patient, onChange }) {
  const [enabled, setEnabled] = useState(patient.nurse_assistance_enabled || false);
  const [days, setDays] = useState(patient.nurse_call_days || []);
  const [dayInput, setDayInput] = useState("");
  const [saving, setSaving] = useState(false);
  const dur = patient.care_plan_duration || 20;

  const toggle = async (v) => {
    setEnabled(v);
    setSaving(true);
    try {
      await api.toggleNurseAssistance(patient.id, v);
      toast.success(v ? "Nurse assistance enabled" : "Nurse assistance disabled");
      onChange && onChange();
    } catch { toast.error("Failed to update"); setEnabled(!v); }
    finally { setSaving(false); }
  };

  const persistDays = async (next) => {
    setDays(next);
    try {
      const res = await api.setNurseCalls(patient.id, next);
      setDays(res.nurse_call_days);
      onChange && onChange();
    } catch { toast.error("Failed to save nurse calls"); }
  };

  const addDay = () => {
    const n = parseInt(dayInput, 10);
    if (!Number.isFinite(n) || n < 1 || n > dur) {
      toast.error(`Enter a day between 1 and ${dur}`);
      return;
    }
    if (days.includes(n)) return;
    setDayInput("");
    persistDays([...days, n].sort((a, b) => a - b));
  };

  const removeDay = (d) => persistDays(days.filter((x) => x !== d));

  return (
    <div
      data-testid="nurse-scheduler"
      className="surface-card p-5 shadow-sm flex flex-col gap-4"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <Phone className="w-4 h-4 text-muted-foreground" />
            <div className="font-semibold text-foreground">Nurse assistance</div>
          </div>
          <div className="text-xs text-muted-foreground mt-1 max-w-sm">
            Route this patient to the nurse queue for follow-up calls. Schedule specific call days on the protocol timeline.
          </div>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={toggle}
          disabled={saving}
          data-testid="nurse-toggle-switch"
          aria-label="Toggle nurse assistance"
        />
      </div>

      {enabled && (
        <div className="border-t border-border pt-4">
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
            Scheduled nurse-call days
          </div>
          <div className="flex flex-wrap gap-1.5 mb-3 min-h-[28px]">
            {days.length === 0 && <span className="text-xs text-muted-foreground italic">No calls scheduled yet.</span>}
            {days.map((d) => (
              <span
                key={d}
                data-testid={`nurse-day-chip-${d}`}
                className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full border border-border bg-secondary text-foreground"
              >
                Day {d}
                <button
                  onClick={() => removeDay(d)}
                  data-testid={`nurse-day-remove-${d}`}
                  className="text-muted-foreground hover:text-rose-500"
                  aria-label={`Remove day ${d}`}
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              min={1}
              max={dur}
              placeholder={`1 – ${dur}`}
              value={dayInput}
              onChange={(e) => setDayInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addDay())}
              data-testid="nurse-day-input"
              className="h-8 text-sm max-w-[110px]"
            />
            <Button
              size="sm" onClick={addDay}
              data-testid="nurse-day-add-btn"
              className="rounded-full h-8 text-xs"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add call
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
